import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useSEO } from '@/hooks/useSEO';
import { siteInfo } from '@/mocks/site';
import ContactForm from './components/ContactForm';

const CONTACT_CARDS = [
  {
    icon: 'ri-mail-send-line',
    label: '一般咨询',
    value: siteInfo.email,
    href: `mailto:${siteInfo.email}`,
    note: '合作、转载与读者反馈',
  },
  {
    icon: 'ri-inbox-archive-line',
    label: '线索与更正',
    value: siteInfo.tipEmail,
    href: `mailto:${siteInfo.tipEmail}`,
    note: '提供线索或申请内容更正',
  },
  {
    icon: 'ri-time-line',
    label: '值守时间',
    value: siteInfo.workingHours,
    href: null,
    note: '非值守时段的来信顺延处理',
  },
];

export default function Contact() {
  const jsonLd = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '联系我们 - 黑料首页',
        description: '通过邮件或在线表单提供线索、申请内容更正，或与我们取得联系。',
        url: `${origin}/contact`,
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteInfo.name,
        email: siteInfo.email,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: siteInfo.email,
          availableLanguage: ['zh-CN'],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: origin || undefined },
          { '@type': 'ListItem', position: 2, name: '联系我们' },
        ],
      },
    ];
  }, []);

  useSEO({
    title: '联系我们 - 提供线索与内容更正 | 黑料首页',
    description:
      '通过邮件或在线表单联系黑料首页编辑部：提供可核实的线索、申请内容更正、咨询合作与转载。值守时间每日 09:00 至 24:00，非值守时段来信顺延处理。',
    keywords: '联系我们,提供线索,内容更正,黑料首页,读者反馈',
    canonicalPath: '/contact',
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-10">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '联系我们' }]} />
            <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              联系我们
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-700">
              无论是提供线索、申请内容更正，还是转载与合作的咨询，都可以通过下面的方式找到编辑部。我们更看重信息的可核实程度，
              而不是线索的数量。
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-shell px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.85fr_1.15fr]">
            <div className="flex flex-col gap-5">
              {CONTACT_CARDS.map((card) => (
                <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                    <i className={card.icon} aria-hidden="true" />
                  </span>
                  <h2 className="mt-3 font-heading text-base font-bold text-foreground-950">{card.label}</h2>
                  {card.href ? (
                    <a
                      href={card.href}
                      className="mt-1 block break-all text-sm font-medium text-primary-700 hover:text-primary-800"
                    >
                      {card.value}
                    </a>
                  ) : (
                    <p className="mt-1 text-sm font-medium text-foreground-900">{card.value}</p>
                  )}
                  <p className="mt-1 text-xs text-foreground-600">{card.note}</p>
                </div>
              ))}

              <div className="rounded-lg border border-accent-200 bg-accent-100 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-question-line text-accent-700" aria-hidden="true" />
                  先看看常见问题？
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                  内容来源、更新机制、更正流程等高频问题，我们已经在问题页完整说明，也许能更快解决你的疑问。
                </p>
                <Link
                  to="/faq"
                  className="mt-4 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-accent-800 hover:text-accent-900"
                >
                  前往常见问题
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <ContactForm />
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}